import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useSocket } from './SocketContext'
import { useAuthStore } from '../services/auth'
import DonationNotificationPopup from '../components/DonationNotificationPopup'

interface DonationNotificationData {
  id: string
  donorName: string
  amount: number
  currency: string
  message?: string
  auctionTitle: string
  charityName: string
  createdAt: string
  auctionId: string
}

interface DonationNotificationContextType {
  showDonationNotification: (data: DonationNotificationData) => void
  clearDonationNotification: (id: string) => void
  notifications: DonationNotificationData[]
}

const DonationNotificationContext = createContext<DonationNotificationContextType | undefined>(undefined)

export const useDonationNotifications = () => {
  const context = useContext(DonationNotificationContext)
  if (!context) {
    throw new Error('useDonationNotifications must be used within a DonationNotificationProvider')
  }
  return context
}

interface DonationNotificationProviderProps {
  children: ReactNode
}

export const DonationNotificationProvider: React.FC<DonationNotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<DonationNotificationData[]>([])
  const { socket } = useSocket()
  const { user } = useAuthStore()

  const showDonationNotification = (data: DonationNotificationData) => {
    // Show donation notification called
    
    // Don't show notification for the donor's own donation
    if (user && data.donorName === user.displayName) {
      // Blocking donation notification - user's own donation
      return
    }

    // Create a new donation notification
    const donationNotification: DonationNotificationData = {
      ...data,
      id: `donation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString()
    }

    // Add to the notifications (limit to 2 donation notifications at once)
    setNotifications(prev => {
      const newNotifications = [...prev, donationNotification]
      return newNotifications.slice(-2) // Keep only the last 2
    })

    // Auto-remove after 45 seconds
    setTimeout(() => {
      clearDonationNotification(donationNotification.id)
    }, 45000)
  }

  const clearDonationNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }

  // Listen for donation events from socket
  useEffect(() => {
    if (!socket) {
      // No socket available for donation notifications
      return
    }

    // Setting up donation notification listeners

    const handleDonationReceived = (data: any) => {
      // Received donation notification
      
      // Transform the notification data to match our interface
      const donationData: DonationNotificationData = {
        id: data.id || `donation-${Date.now()}`,
        donorName: data.donorName || data.donor?.displayName || 'Anonymous Donor',
        amount: data.amount,
        currency: data.currency || 'USD',
        message: data.message,
        auctionTitle: data.auctionTitle || data.auction?.title || 'Charity Auction',
        charityName: data.charityName || data.charity || 'Charity',
        createdAt: data.createdAt || new Date().toISOString(),
        auctionId: data.auctionId
      }
      
      showDonationNotification(donationData)
    }

    // Listen to donation events
    socket.on('donation_received', handleDonationReceived)
    socket.on('donationReceived', handleDonationReceived) // Alternative event name

    return () => {
      socket.off('donation_received', handleDonationReceived)
      socket.off('donationReceived', handleDonationReceived)
    }
  }, [socket, user])

  const value = {
    showDonationNotification,
    clearDonationNotification,
    notifications,
  }

  return (
    <DonationNotificationContext.Provider value={value}>
      {children}
      {/* Render donation notification popups */}
      {notifications.map((notification) => (
        <DonationNotificationPopup
          key={notification.id}
          notification={notification}
          onClose={() => clearDonationNotification(notification.id)}
        />
      ))}
    </DonationNotificationContext.Provider>
  )
}

export default DonationNotificationProvider
